/**
 * Вебманіфести модулів (module-contract.md, розд. 5.8): оболонка збирає їх під час збірки —
 * `import.meta.glob` за каталогами `modules/*\/web/manifest.ts`. Ядро не знає конкретних модулів:
 * новий модуль з'являється в навігації, слотах картки й командах сам. Каталоги підписів модулів
 * маленькі й потрібні першому екрану (пункти навігації), тож вантажаться разом із маніфестами;
 * код внесків і команд — лише ліниво. Тут — те, що бере перший екран (навігація, підписи); вибір
 * внесків слотів, рендерерів лінії й команд запису — у `module-record.ts` (лише ліниві чанки картки).
 */

import type { AppMeta } from '@clientline/contracts';
import type { SlotRecord, WebModuleManifest, WebText } from '@clientline/contracts/web-module';

const manifests = import.meta.glob<WebModuleManifest>('../../../../modules/*/web/manifest.ts', {
  eager: true,
  import: 'default',
});
const catalogs = import.meta.glob<Record<string, string>>('../../../../modules/*/i18n/uk.ts', {
  eager: true,
  import: 'default',
});

const moduleOf = (path: string) => /modules\/([^/]+)\//.exec(path)?.[1] ?? '';

export const WEB_MODULES: readonly WebModuleManifest[] = Object.values(manifests);
const CATALOGS = new Map(Object.entries(catalogs).map(([path, c]) => [moduleOf(path), c]));

/** Підпис модуля: `i18n:<ключ>` — з його каталогу; без перекладу — сам ключ (видно в тестах). */
export function moduleText(moduleId: string, text: WebText | undefined): string {
  if (!text) return '';
  if (typeof text !== 'string') return text.uk;
  const key = text.slice('i18n:'.length);
  return CATALOGS.get(moduleId)?.[key] ?? key;
}

/** Модуль увімкнено в `/meta`; право внеску (`permission`) — у людини. Спільне з `module-record.ts`. */
export const moduleEnabled = (meta: AppMeta, m: WebModuleManifest) => meta.modules.includes(m.id);
export const modulePermitted = (meta: AppMeta, permission: string | undefined) =>
  permission === undefined || meta.permissions.includes(permission);

export function navItems(meta: AppMeta) {
  return WEB_MODULES.filter((m) => moduleEnabled(meta, m)).flatMap((m) =>
    (m.nav ?? [])
      .filter((item) => modulePermitted(meta, item.permission))
      .map((item) => ({ ...item, module: m.id, text: moduleText(m.id, item.label) })),
  );
}

export type { SlotRecord };
